import { formatCurrency } from "@/lib/format"
import {
  buildFallback,
  PRICE_KEY_FOR_SLUG,
  type PriceData,
} from "@/lib/pricing"

import type {
  MembershipTier,
  PricingAddOn,
  PricingCardData,
  PricingCardVariant,
  PricingCardWire,
  PricingRow,
  PricingTab,
} from "@/components/chat/pricing-breakdown"

/**
 * Widget-owned presentation for the pricing card (AHD-11297, decision A). glue
 * emits only the authoritative package prices; names, inclusions, the blood-test
 * + add-on rows, and the test-page links live here so product strings stay in
 * marketing-site (same split as the biomarker card).
 *
 * Content reuses the EXISTING localized `pricing`-namespace keys the pricing
 * page already ships (`MRI_PACKAGES` features + `data/product-info.ts` name
 * keys), so the card is fully localized with no new content translation — the
 * caller passes a `pricing`-namespace translator. Prices are real: packages from
 * glue, the blood test + add-ons from the site's own feed.
 *
 * Biomarker counts are intentionally omitted in v1 — the authoritative count is
 * CMS-derived server-side and this card renders client-side from the SSE turn;
 * wiring a build-time count snapshot is a fast-follow. (The blood test's first
 * inclusion already conveys "80+ biomarkers".)
 */

/** A `pricing`-namespace translator, widened for dynamic keys (as MarkerBreakdown
 *  does for `whatWeDetect`). */
export type PricingTranslator = (key: string) => string

/** Biomarker counts per package id, from `/api/package-counts` (CMS-derived). */
export interface PackageCounts {
  core?: number
  advanced?: number
  pro?: number
  blood?: number
}

const countsPromises = new Map<string, Promise<PackageCounts>>()

/**
 * Fetch package biomarker counts from the same-origin, hard-cached route
 * (memoized per session — counts are locale-invariant and near-static). Returns
 * {} on the server or on any failure, so the card simply renders without counts
 * rather than blocking or fabricating one (they fill in on the client).
 *
 * Memoized PER COUNTRY (AHD-12853): the panel differs per market, so a single
 * shared promise would serve whichever market rendered the first card to every
 * later one. Chat runs in CH and NL, so that is reachable, not theoretical.
 */
export function fetchPackageCounts(country: string): Promise<PackageCounts> {
  if (typeof window === "undefined") return Promise.resolve({})
  const cached = countsPromises.get(country)
  if (cached) return cached
  const promise = fetch(
    `/api/package-counts?country=${encodeURIComponent(country)}`
  )
    .then((r) => (r.ok ? (r.json() as Promise<PackageCounts>) : {}))
    .catch(() => ({}))
  countsPromises.set(country, promise)
  return promise
}

type PackageId = "core" | "advanced" | "pro"

/**
 * Package → localized name key + inclusion keys (mirrors `MRI_PACKAGES`).
 *
 * `showCount`: the biomarker count is a headline only for blood-inclusive
 * products. Core is MRI-only (no blood panel), so its CMS biomarker count is ~2
 * and displaying it *undersells* a full-body MRI — hide it. Advanced and Pro
 * add the blood test (real counts ~95 / ~125), so the count is a genuine selling
 * point there. (Product can flip these flags.)
 */
const PACKAGE_META: Record<
  PackageId,
  { nameKey: string; featureKeys: string[]; showCount: boolean }
> = {
  core: {
    nameKey: "piNameCore",
    featureKeys: ["featureFullBodyMri", "featureBrainMri", "featureSpineMri"],
    showCount: false,
  },
  advanced: {
    nameKey: "piNameAdvanced",
    featureKeys: [
      "featureAdvancedBloodTest",
      "featureBodyComposition",
      "featureProstateOvariesMri",
    ],
    showCount: true,
  },
  pro: {
    nameKey: "piNamePro",
    featureKeys: [
      "featureBrainVolumetry",
      "featureHipsKneesMri",
      "featureHormonesVitamins",
    ],
    showCount: true,
  },
}

/** The standalone blood check-up — the entry product, widget-owned. */
const BLOOD_TEST = {
  nameKey: "advancedBloodTestTitle",
  featureKeys: ["piAdvancedBloodTestBenefit1", "piAdvancedBloodTestBenefit2"],
  slug: "advanced-blood-test",
}

/** A translator over the `_membership` namespace (widened for dynamic keys), for
 *  the tier names/tests strings the membership card reuses. */
export type MembershipTranslator = (key: string) => string

/**
 * The three membership tiers (AHD-11637). Amounts are CHF whole units, rendered
 * through `formatCurrency` + the localized `/year` suffix — the same
 * widget-owned, CH-gated pattern `MembershipFrequencySection` uses (membership
 * tiers are NOT Stripe products). Source of truth: the membership page frequency
 * section / glue `price-guard`'s `NON_STRIPE_VALID_CHF`. Names + the tests
 * descriptor reuse the existing localized `_membership` frequency keys, so the
 * card needs no new membership copy. No tier is featured by default — the
 * membership page markets none as "most popular", so the card invents no
 * positioning.
 */
const MEMBERSHIP_TIERS: {
  id: string
  chf: number
  nameKey: string
  testsKey: string
}[] = [
  {
    id: "1x",
    chf: 299,
    nameKey: "membership_frequency_baseline_name",
    testsKey: "membership_frequency_baseline_tests",
  },
  {
    id: "2x",
    chf: 499,
    nameKey: "membership_frequency_2x_name",
    testsKey: "membership_frequency_2x_tests",
  },
  {
    id: "4x",
    chf: 899,
    nameKey: "membership_frequency_4x_name",
    testsKey: "membership_frequency_4x_tests",
  },
]

const MEMBERSHIP_VARIANTS = new Set<PricingCardVariant>([
  "overview",
  "membership",
])
const BLOOD_VARIANTS = new Set<PricingCardVariant>(["overview", "blood"])

/** The add-ons the overview card surfaces, by stable test slug (→ `/tests/<slug>`).
 *  A curated few of the full catalog — the card links to the pricing page for
 *  the rest (`addOnsHref`). A focused turn shows the add-ons glue named instead.
 *  Every add-on requires a base (blood test or MRI), so the card states no
 *  per-row requirement; the test/pricing pages carry it. */
const CARD_ADDONS: string[] = [
  "calcium-score",
  "dexa-scan",
  "hormone-blood-test-women",
]

const PRICING_VARIANTS = new Set<PricingCardVariant>([
  "overview",
  "package",
  "addon",
  "blood",
  "membership",
])
const PRICING_TABS = new Set<PricingTab>(["scans", "blood", "membership"])

/**
 * Resolve a glue add-on slug to a localized, priced card row. The name and the
 * one-line description both come from the `pricing` catalog, keyed by the same
 * price-map key the slug maps to (so `dexa-scan` → `addonDEXA` / `addonDEXADesc`);
 * the price comes from the site's own feed. Returns null when the slug is unknown
 * or its price is genuinely absent — never a placeholder.
 */
function resolveAddon(
  slug: string,
  base: string,
  t: PricingTranslator,
  feed: PriceData,
  withDescription: boolean
): PricingAddOn | null {
  const key = PRICE_KEY_FOR_SLUG[slug]
  if (!key) return null
  const priceLabel = feed.fullPrices[key]
  if (!priceLabel) return null
  return {
    name: t(key),
    priceLabel,
    href: `${base}/tests/${slug}`,
    ...(withDescription ? { description: t(`${key}Desc`) } : {}),
  }
}

const PACKAGE_ORDER: PackageId[] = ["core", "advanced", "pro"]

/** All three MRI tiers share the full-body-MRI detail page (PACKAGE_TO_TEST_SLUG). */
const MRI_TEST_SLUG = "full-body-mri"

/**
 * Build the render model from glue's wire payload. Packages render glue's
 * authoritative CHF (formatted here — never a currency literal); the blood test
 * and add-ons are appended from the site's own feed. Names + inclusions resolve
 * through the passed `pricing`-namespace translator, so the card is localized. A
 * row whose price is genuinely absent is dropped, never shown with a placeholder.
 */
export function buildPricingCardData(
  wire: PricingCardWire,
  t: PricingTranslator,
  counts: PackageCounts = {},
  membershipT?: MembershipTranslator
): PricingCardData {
  const { country, locale } = wire
  const base = `/${country}/${locale}`
  const feed = buildFallback(country)
  const isCH = feed.currency === "CHF"
  // Forward-compatible: an absent/unknown variant renders the overview.
  const variant: PricingCardVariant = PRICING_VARIANTS.has(
    wire.variant as PricingCardVariant
  )
    ? (wire.variant as PricingCardVariant)
    : "overview"
  const focus: PricingTab = PRICING_TABS.has(wire.focus as PricingTab)
    ? (wire.focus as PricingTab)
    : "scans"
  const priceById = new Map(wire.packages.map((p) => [p.id, p.priceChf]))

  // Packages (MRI scans): none for the add-on / blood / membership focused
  // variants; otherwise the selected set (glue already filtered `wire.packages`
  // to what the turn asked about). Blood is now its own entity, not a row here.
  const packages: PricingRow[] = []
  if (variant === "overview" || variant === "package") {
    for (const id of PACKAGE_ORDER) {
      const chf = priceById.get(id)
      if (chf == null) continue
      const meta = PACKAGE_META[id]
      const count = meta.showCount ? counts[id] : undefined
      packages.push({
        id,
        name: t(meta.nameKey),
        priceLabel: formatCurrency(chf, country),
        ...(count ? { biomarkerCount: count } : {}),
        expectsCount: meta.showCount,
        highlights: meta.featureKeys.map((k) => t(k)),
        href: `${base}/tests/${MRI_TEST_SLUG}`,
        featured: id === "advanced",
      })
    }
  }

  // The standalone blood check-up — first-class on the overview's Blood tab and
  // the `blood` focused hero. Priced from the site's own feed (a real figure on
  // EUR markets too, from the market-tagged feed). Dropped if its price is absent.
  let blood: PricingRow | undefined
  if (BLOOD_VARIANTS.has(variant)) {
    const bloodLabel = feed.fullPrices.blood
    if (bloodLabel) {
      blood = {
        id: "blood",
        name: t(BLOOD_TEST.nameKey),
        priceLabel: bloodLabel,
        ...(counts.blood ? { biomarkerCount: counts.blood } : {}),
        expectsCount: true,
        highlights: BLOOD_TEST.featureKeys.map((k) => t(k)),
        href: `${base}/tests/${BLOOD_TEST.slug}`,
      }
    }
  }

  // Membership tiers — the overview's Membership tab and the `membership` focused
  // card. CH-only: the tier amounts are widget-owned CHF literals, so they never
  // render on a EUR market (no currency leak). Needs the `_membership` translator
  // for the localized tier names; omitted if it wasn't passed.
  let membership: MembershipTier[] | undefined
  let membershipFromLabel: string | undefined
  let membershipSubtitle: string | undefined
  if (MEMBERSHIP_VARIANTS.has(variant) && isCH && membershipT) {
    const perYear = membershipT("membership_frequency_per_year_suffix")
    membership = MEMBERSHIP_TIERS.map((tier) => ({
      id: tier.id,
      name: membershipT(tier.nameKey),
      priceLabel: `${formatCurrency(tier.chf, country)}${perYear}`,
      tests: membershipT(tier.testsKey),
    }))
    membershipFromLabel = `${formatCurrency(
      Math.min(...MEMBERSHIP_TIERS.map((tr) => tr.chf)),
      country
    )}${perYear}`
    if (variant === "membership") {
      membershipSubtitle = membershipT("membership_hero_subtitle")
    }
  }

  // Add-ons: the overview shows the curated few; a focused add-on turn shows
  // exactly what glue named — with the one-line description on the add-on-only
  // variant, where the add-on is the answer rather than a supporting row.
  const addonSlugs = variant === "overview" ? CARD_ADDONS : (wire.addons ?? [])
  const addOns = addonSlugs
    .map((slug) => resolveAddon(slug, base, t, feed, variant === "addon"))
    .filter((a): a is PricingAddOn => a !== null)

  // The overview folds its curated add-ons into a single "from CHF X" link to
  // keep the card compact; the anchor price is the cheapest curated add-on, from
  // the site's own numeric feed. Absent when no add-on price resolves.
  let addOnsFromLabel: string | undefined
  if (variant === "overview") {
    const addonPrices = CARD_ADDONS.map((slug) => PRICE_KEY_FOR_SLUG[slug])
      .map((key) => (key ? Number(feed.prices[key]) : NaN))
      .filter((n) => Number.isFinite(n))
    if (addonPrices.length > 0) {
      addOnsFromLabel = formatCurrency(Math.min(...addonPrices), country)
    }
  }

  return {
    country,
    locale,
    currency: feed.currency,
    variant,
    focus,
    packages,
    ...(blood ? { blood } : {}),
    ...(membership ? { membership } : {}),
    ...(membershipFromLabel ? { membershipFromLabel } : {}),
    ...(membershipSubtitle ? { membershipSubtitle } : {}),
    ...(membership ? { membershipHref: `${base}/membership` } : {}),
    addOns,
    ...(addOnsFromLabel ? { addOnsFromLabel } : {}),
    // The card curates a few add-ons; this links to the full catalog.
    addOnsHref: `${base}/pricing`,
    compareHref: `${base}/pricing`,
  }
}
